import { Box, Flex, Heading, Text, Link } from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { NextSeo } from 'next-seo';
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { GetServerSideProps } from 'next';
import { ApolloClient, InMemoryCache } from '@apollo/client';

import HeaderMenu from '@/components/HeaderMenu';
import VerticalCardImg from '@/components/VerticalCardImg';
import Footer from '@/components/Footer';
import { handleError } from '@/helpers/handleError';

import { GET_CATEGORY_PAGINATION, GET_CATEGORY } from '@/graphql/queries';
import { BlogPostsMainData } from '@/Types/types';
import { configFile } from '@/config';

export default function CategoryPage({ categoryPosts, category, cookies, header, footer }: any) {
  const [pageUrl, setPageUrl] = useState<any>();
  const [page, setPage] = useState<number>();
  const [counter, setCounter] = useState<number>();
  const [pageCount, setPageCount] = useState<number>();
  const [prevHref, setPrevHref] = useState<string>();
  const [isPrevDisabled, setIsPrevDisabled] = useState<boolean>();
  const [isNextDisabled, setIsNextDisabled] = useState<boolean>();
  const [isPagination, setIsPagination] = useState<boolean>();
  const router = useRouter();

  useEffect(() => {
    setIsPrevDisabled(page === 1);

    setIsNextDisabled(page === categoryPosts.meta.pagination.pageCount);

    if (pageUrl !== undefined) {
      if (page !== 1) {
        router.push(
          {
            pathname: `/categoria/${category.data[0].attributes.slug}`,
            query: { page },
          },
          `/categoria/${category.data[0].attributes.slug}?page=${page}`,
          { shallow: false },
        );
      }

      if (page === 1) {
        router.push(
          {
            pathname: `/categoria/${category.data[0].attributes.slug}`,
          },
          `/categoria/${category.data[0].attributes.slug}`,
          { shallow: false },
        );
      }
    }
  }, [page]);

  const handelClick = useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });

    if (page === 2) {
      setPrevHref(`/categoria/${category.data[0].attributes.slug}`);
    } else if (page === 1) {
      setPrevHref('');
    } else {
      setPrevHref(`?page=${page! - 1}`);
    }
  }, [page]);

  useEffect(() => {
    setPageUrl(category.data[0].attributes.slug);
    setCounter(categoryPosts.meta.pagination.total);
    setPageCount(categoryPosts.meta.pagination.pageCount);
    setIsPagination(categoryPosts.meta.pagination.pageCount > 1);

    if (router.isReady) {
      setPage(+(router.query.page as unknown as number) || 1);
    }
  }, [router.query.slug, router.query.page]);

  return (
    <>
      <HeaderMenu cookies={cookies.data} menu={header[0].items} />
      <NextSeo
        title={category.data[0].attributes.metaTitle}
        description={category.data[0].attributes.metaDescription}
        canonical={category.data[0].attributes.canonicalURL}
      />
      <Box maxWidth="1216px" margin="0 auto" width="90%" mb="32px" as="header" pt={{ base: '60px', lg: '80px' }}>
        <Heading mb="32px" as="h1" fontWeight="800" position="relative">
          {category.data[0]?.attributes.title}
          <Text
            as="span"
            bgColor={configFile.primaryAccentColor}
            height="20px"
            color="#fff"
            display="inline-flex"
            fontWeight="700"
            fontSize="12px"
            lineHeight="16px"
            justifyContent="center"
            alignItems="center"
            position="relative"
            ml="20px"
            top="-5px"
            padding="0 5px"
          >
            {counter}
          </Text>
        </Heading>
        <Text color="gray.600" fontSize="20px" lineHeight="28px">
          {category.data[0]?.attributes.description}
        </Text>
      </Box>
      <Flex
        maxWidth="1216px"
        margin="0 auto"
        width="90%"
        columnGap="32px"
        rowGap="32px"
        flexWrap="wrap"
        pb={{ base: '44px', lg: '64px' }}
        justifyContent="flex-start"
      >
        {categoryPosts.data.map((item: BlogPostsMainData, index: number) => (
          <VerticalCardImg notMainPage key={index} item={item} />
        ))}
      </Flex>
      {isPagination && (
        <Flex
          padding="12px"
          bgColor="#fff"
          columnGap={{ base: '16px', md: '32px' }}
          border="1px solid #E2E8F0"
          borderRadius="6px"
          alignItems="center"
          width="fit-content"
          margin="0 auto"
          mb="80px"
        >
          <Link
            href={prevHref}
            className={isPrevDisabled ? 'disabled' : ''}
            onClick={(e) => [e.preventDefault(), setPage(page! - 1), handelClick]}
            width={{ base: '90px', md: '150px' }}
            display="flex"
            alignItems="center"
            bgColor={configFile.primaryAccentColor}
            height="40px"
            justifyContent="center"
            borderRadius="6px"
            lineHeight="1"
            fontWeight="600"
            fontSize={{ base: '13px', md: '16px' }}
            color="#fff"
            border="1px solid transparent"
            _hover={{
              color: 'gray.800',
              bgColor: '#fff',
              borderColor: '#E2E8F0',
            }}
            _disabled={{ bgColor: '#fff', borderColor: '#E2E8F0' }}
          >
            <ChevronLeftIcon />
            Previous
          </Link>
          <Text fontSize={{ base: '13px', md: '16px' }} color="gray.500" fontWeight="400">
            Page {page} of {pageCount}
          </Text>
          <Link
            href={isNextDisabled ? '' : `?page=${page! + 1}`}
            className={isNextDisabled ? 'disabled' : ''}
            onClick={(e) => [e.preventDefault(), setPage(page! + 1), handelClick]}
            width={{ base: '90px', md: '150px' }}
            display="flex"
            justifyContent="center"
            borderRadius="6px"
            height="40px"
            alignItems="center"
            bgColor={configFile.primaryAccentColor}
            lineHeight="1"
            fontWeight="600"
            fontSize={{ base: '13px', md: '16px' }}
            border="1px solid transparent"
            color="#fff"
            _hover={{
              color: 'gray.800',
              bgColor: '#fff',
              borderColor: '#E2E8F0',
            }}
            _disabled={{
              color: 'gray.800',
              bgColor: '#fff',
              borderColor: '#E2E8F0',
            }}
          >
            Next <ChevronRightIcon />
          </Link>
        </Flex>
      )}
      <Footer menu={footer[0].items} />
    </>
  );
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  try {
    context.res.setHeader('Cache-Control', 'public, s-maxage=10, stale-while-revalidate=59');

    const client = new ApolloClient({
      uri: `${process.env.NEXT_PUBLIC_API_URL}/graphql`,
      cache: new InMemoryCache(),
    });

    const { data: categoryData } = await client.query({
      query: GET_CATEGORY,
      variables: {
        slugUrl: context.params?.slug,
      },
    });

    if (!categoryData.category.data.length) {
      return {
        notFound: true,
      };
    }

    const { data: categoryPosts } = await client.query({
      query: GET_CATEGORY_PAGINATION,
      variables: {
        slugUrl: context.params?.slug,
        /* eslint-disable */
        page: (context.query?.page && +context.query?.page) || 1,
      },
    });

    return {
      props: { ...categoryPosts, ...categoryData },
    };
  } catch (error) {
    handleError(error, 'Categoria Slug getServerSideProps');

    return {
      notFound: true,
    };
  }
};
